
import React from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { 
  Star, 
  Award, 
  CheckCircle,
  Shield
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface TestimonialProps {
  quote: string;
  name: string;
  role: string;
  avatar?: string;
}

const Testimonial = ({ quote, name, role, avatar }: TestimonialProps) => {
  return (
    <div className="bg-white rounded-lg p-6 shadow-md">
      <div className="flex items-center gap-1 text-legatia-yellow mb-4">
        <Star className="fill-legatia-yellow text-legatia-yellow h-5 w-5" />
        <Star className="fill-legatia-yellow text-legatia-yellow h-5 w-5" />
        <Star className="fill-legatia-yellow text-legatia-yellow h-5 w-5" />
        <Star className="fill-legatia-yellow text-legatia-yellow h-5 w-5" />
        <Star className="fill-legatia-yellow text-legatia-yellow h-5 w-5" />
      </div>
      
      <p className="italic text-gray-600 mb-6">&ldquo;{quote}&rdquo;</p>
      
      <div className="flex items-center">
        <Avatar className="h-10 w-10 mr-3">
          <AvatarImage src={avatar} alt={name} />
          <AvatarFallback>{name.charAt(0)}</AvatarFallback>
        </Avatar>
        <div>
          <p className="font-semibold">{name}</p>
          <p className="text-sm text-gray-500">{role}</p>
        </div>
      </div>
    </div>
  );
};

const TrustBadge = ({ icon, text }: { icon: React.ReactNode, text: string }) => {
  return (
    <div className="flex items-center gap-3 bg-white p-3 rounded-lg shadow-sm">
      {icon}
      <span className="text-sm font-medium">{text}</span>
    </div>
  );
};

const SocialProofSection = () => {
  const { language } = useLanguage();
  
  const content = {
    fr: {
      title: "Des cadeaux pour les moments qui changent une vie",
      testimonials: [
        {
          quote: "Grâce à Legatia, j'ai pu offrir à ma petite-fille un cadeau pour ses études qui a pris de la valeur pendant 18 ans. Son émotion en découvrant mon message était inestimable.",
          name: "Marie D.",
          role: "Grand-mère de deux enfants"
        },
        {
          quote: "Nous avons créé un compte pour les 25 ans de notre fils. Le capital constitué l'aidera pour son premier achat immobilier, exactement quand il en aura besoin.",
          name: "Thomas L.",
          role: "Père d'un jeune adulte"
        }
      ],
      trustBadges: [
        "Sécurité suisse",
        "Réglementé financièrement",
        "Cryptage de bout en bout"
      ]
    },
    en: {
      title: "Gifts for Life-Changing Moments",
      testimonials: [
        {
          quote: "Thanks to Legatia, I was able to give my granddaughter a gift for her studies that grew in value for 18 years. Her emotion when discovering my message was priceless.",
          name: "Marie D.",
          role: "Grandmother of two"
        },
        {
          quote: "We created an account for our son's 25th birthday. The capital built will help him with his first property purchase, exactly when he needs it.",
          name: "Thomas L.",
          role: "Father of a young adult"
        }
      ],
      trustBadges: [
        "Swiss security",
        "Financially regulated",
        "End-to-end encryption"
      ]
    },
    de: {
      title: "Geschenke für lebensverändernde Momente",
      testimonials: [
        {
          quote: "Dank Legatia konnte ich meiner Enkelin ein Geschenk für ihr Studium machen, das 18 Jahre lang an Wert gewonnen hat. Ihre Emotionen beim Entdecken meiner Nachricht waren unbezahlbar.",
          name: "Marie D.",
          role: "Großmutter von zwei Kindern"
        },
        {
          quote: "Wir haben ein Konto für den 25. Geburtstag unseres Sohnes erstellt. Das aufgebaute Kapital wird ihm beim ersten Immobilienkauf helfen, genau dann, wenn er es braucht.",
          name: "Thomas L.",
          role: "Vater eines jungen Erwachsenen"
        }
      ],
      trustBadges: [
        "Schweizer Sicherheit",
        "Finanziell reguliert",
        "Ende-zu-Ende-Verschlüsselung"
      ]
    }
  };
  
  const textContent = content[language];
  
  const badgeIcons = [
    <Shield className="h-5 w-5 text-legatia-orange" />,
    <Award className="h-5 w-5 text-legatia-blue" />,
    <CheckCircle className="h-5 w-5 text-legatia-green" />
  ];

  return (
    <section className="py-20 px-4 bg-gray-50">
      <div className="container mx-auto max-w-6xl">
        <h2 className="section-title text-center mb-16">
          {textContent.title}
        </h2>
        
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {textContent.testimonials.map((testimonial, index) => (
            <Testimonial
              key={index}
              quote={testimonial.quote}
              name={testimonial.name}
              role={testimonial.role}
              avatar={index === 0 ? "/assets/testimonial-1.jpg" : "/assets/testimonial-2.jpg"}
            />
          ))}
        </div>
        
        <div className="flex flex-wrap justify-center gap-4">
          {textContent.trustBadges.map((badge, index) => (
            <TrustBadge 
              key={index} 
              icon={badgeIcons[index]} 
              text={badge} 
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default SocialProofSection;
